import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { FaFileAlt, FaDownload, FaTrash, FaImage, FaUser, FaTags, FaListAlt, FaCalendarAlt, FaCommentDots } from "react-icons/fa";
import CommonModal from "Components/CommonModal";
import { getFileDownload, getResizeFileDownload, getSelfDetail, selfMultiDelete } from "JS/API";
import { markTypeList } from "JS/array";
import { catchError, commonModalSetting } from "JS/common";
import FloatingList from "Components/FloatingList";
import { detailPageStyles } from "utils/designSystem";

const SelfApplicationDetail = () => {
  const [info, setInfo] = useState(null);
  const [alertMsg, setAlertMsg] = useState("");
  const [alertBox, setAlertBox] = useState({
    mode: "",
    context: "",
    bool: false
  });

  const [floatSideBar, setFloatSideBar] = useState(false);
  const [imageUrl, setImageUrl] = useState("");
  const [originalImageUrl, setOriginalImageUrl] = useState("");
  const [isMobile, setIsMobile] = useState(false);

  const { id } = useParams();
  const navigate = useNavigate();

  // 화면 크기 감지
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 1024); // 768px에서 1024px로 변경
    };

    handleResize(); // 초기값 설정
    window.addEventListener("resize", handleResize);

    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // 스타일 정의
  const styles = {
    container: {
      padding: "24px",
      paddingRight: isMobile ? "24px" : "374px", // PC에서는 FloatingList 공간 확보 (350px + 24px)
      backgroundColor: "#f1f5f9",
      minHeight: "100vh"
    },
    header: detailPageStyles.header,
    title: detailPageStyles.title,
    idBadge: detailPageStyles.idBadge,
    section: detailPageStyles.section,
    sectionHeader: {
      padding: "16px 24px",
      borderBottom: "1px solid #e2e8f0",
      backgroundColor: "#f8fafc"
    },
    sectionTitle: detailPageStyles.sectionTitle,
    sectionContent: detailPageStyles.sectionContent,
    tableWrapper: detailPageStyles.tableWrapper,
    table: detailPageStyles.table,
    th: detailPageStyles.th,
    td: detailPageStyles.td,
    tdTitle: detailPageStyles.tdTitle,
    imageContainer: {
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      gap: "20px",
      padding: "24px"
    },
    imagePreview: {
      maxWidth: "400px",
      width: "100%",
      height: "auto",
      borderRadius: "8px",
      border: "1px solid #e2e8f0",
      boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)"
    },
    downloadButton: {
      display: "flex",
      alignItems: "center",
      gap: "8px",
      padding: "10px 16px",
      backgroundColor: "#3b82f6",
      color: "#ffffff",
      border: "none",
      borderRadius: "6px",
      fontSize: "14px",
      fontWeight: "500",
      cursor: "pointer",
      transition: "background-color 0.2s ease"
    },
    productTag: {
      display: "inline-block",
      padding: "4px 8px",
      margin: "2px",
      backgroundColor: "#f3f4f6",
      border: "1px solid #d1d5db",
      borderRadius: "4px",
      fontSize: "12px",
      color: "#374151"
    },
    productTagNonRegistered: {
      backgroundColor: "#fef2f2",
      borderColor: "#fecaca",
      color: "#dc2626"
    },
    productContainer: {
      marginTop: "8px"
    },
    productGroup: {
      marginBottom: "16px"
    },
    productLabel: {
      fontWeight: "600",
      marginBottom: "8px",
      display: "block"
    },
    productLabelRegistered: {
      color: "#059669"
    },
    productLabelNonRegistered: {
      color: "#dc2626"
    },
    productTags: {
      display: "flex",
      flexWrap: "wrap",
      gap: "4px",
      alignItems: "center"
    },
    actionSection: {
      display: "flex",
      justifyContent: "flex-end",
      gap: "12px",
      marginTop: "24px"
    },
    deleteButton: {
      display: "flex",
      alignItems: "center",
      gap: "8px",
      padding: "10px 16px",
      backgroundColor: "#dc2626",
      color: "#ffffff",
      border: "none",
      borderRadius: "6px",
      fontSize: "14px",
      fontWeight: "500",
      cursor: "pointer",
      transition: "background-color 0.2s ease"
    },
    markTypeText: {
      padding: "4px 8px",
      borderRadius: "4px",
      fontSize: "12px",
      fontWeight: "500",
      backgroundColor: "#dbeafe",
      color: "#1d4ed8"
    },
    floatingButton: {
      position: "fixed",
      bottom: "24px",
      right: "24px",
      width: "64px",
      height: "64px",
      borderRadius: "50%",
      border: "none",
      background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
      boxShadow: "0 8px 25px rgba(102, 126, 234, 0.4), 0 4px 12px rgba(0, 0, 0, 0.15)",
      cursor: "pointer",
      zIndex: 1000,
      transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
      // 모바일이 아니거나 FloatingList가 열려있으면 숨김
      display: !isMobile || floatSideBar ? "none" : "flex",
      alignItems: "center",
      justifyContent: "center",
      // 숨김/보임 애니메이션
      opacity: !isMobile || floatSideBar ? 0 : 1,
      transform: !isMobile || floatSideBar ? "scale(0) translateY(20px)" : "scale(1) translateY(0)",
      pointerEvents: !isMobile || floatSideBar ? "none" : "auto"
    }
  };

  const getSelfApplicationInfo = async () => {
    const result = await getSelfDetail(id);
    if (typeof result === "object") {
      setInfo(result?.data);
    } else {
      return await catchError(result, navigate, setAlertBox, setAlertMsg);
    }
  };

  useEffect(() => {
    getSelfApplicationInfo();
    document.title = `마크픽 관리자 > 셀프 출원 상세 (ID: ${id})`;
  }, [id]);

  const renderApplicants = () => {
    return info?.applicants?.map((applicant, idx) => (
      <tr key={idx}>
        <td style={styles.td}>{idx + 1}</td>
        <td style={styles.td}>{applicant.applicant_name}</td>
        <td style={styles.td}>{applicant.applicant_code}</td>
        <td style={styles.td}>{applicant.applicant_share} / 100</td>
      </tr>
    ));
  };

  const renderMarks = () => {
    return info?.marks?.map((mark, idx) => (
      <tr key={idx}>
        <td style={styles.td}>{mark.mark_step}</td>
        <td style={styles.td}>{mark.mark_name}</td>
      </tr>
    ));
  };

  const renderDetails = () => {
    return info?.detail_items?.map((item, idx) => (
      <tr key={idx}>
        <td style={styles.td}>{item.mark_class}류</td>
        <td style={styles.td}>
          <div style={styles.productContainer}>
            {/* 고시 상품 */}
            {item.notice_register_product.length > 0 && (
              <div style={styles.productGroup}>
                <span
                  style={{
                    ...styles.productLabel,
                    ...styles.productLabelRegistered
                  }}
                >
                  고시 상품 ({item.notice_register_product.length})
                </span>
                <div style={styles.productTags}>
                  {item.notice_register_product.map((product, index) => (
                    <span key={index} style={styles.productTag}>
                      {product}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* 비고시 상품 */}
            {item.none_register_product.length > 0 && (
              <div style={styles.productGroup}>
                <span
                  style={{
                    ...styles.productLabel,
                    ...styles.productLabelNonRegistered
                  }}
                >
                  비고시 상품 ({item.none_register_product.length})
                </span>
                <div style={styles.productTags}>
                  {item.none_register_product.map((product, index) => (
                    <span
                      key={index}
                      style={{
                        ...styles.productTag,
                        ...styles.productTagNonRegistered
                      }}
                    >
                      {product}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </td>
      </tr>
    ));
  };

  useEffect(() => {
    if (!info) return;

    if (info?.image_filename && info?.image_original_filename) {
      (async () => {
        const base64Url = await fetchUploadedImage(info.image_filename, info.image_original_filename);
        if (base64Url) setOriginalImageUrl(base64Url);
      })();
    }
    if (info?.mark_type === "figure" || info?.mark_type === "text") {
      if (info?.image_filename && info?.image_original_filename) {
        (async () => {
          const base64Url = await fetchUploadedImage(info.image_filename, info.image_original_filename);
          if (base64Url) setImageUrl(base64Url);
        })();
      }
    } else if (info?.mark_type === "compound") {
      if (info?.compound_image_filename && info?.compound_original_filename) {
        (async () => {
          const base64Url = await fetchUploadedImage(info.compound_image_filename, info.compound_original_filename);
          if (base64Url) setImageUrl(base64Url);
        })();
      }
    }
  }, [info]);

  const fetchUploadedImage = async (file_name, original_file_name) => {
    try {
      const response = await getResizeFileDownload(file_name, original_file_name);
      const base64 = btoa(new Uint8Array(response.data).reduce((data, byte) => data + String.fromCharCode(byte), ""));
      const mimeType = response.headers["content-type"];
      return `data:${mimeType};base64,${base64}`;
    } catch (error) {
      console.error("이미지 불러오기 실패:", error);
      return null;
    }
  };

  const renderImage = () => {
    if (!info) return null;

    return (
      <div style={styles.section}>
        <div style={styles.sectionHeader}>
          <h5 style={styles.sectionTitle}>
            <FaImage />
            상표 이미지
          </h5>
        </div>
        <div style={styles.imageContainer}>
          {imageUrl && <img src={imageUrl} alt="상표 이미지" style={styles.imagePreview} />}
          <div style={{ display: "flex", gap: "12px", flexWrap: "wrap" }}>
            <button
              onClick={() => handleImgDownload()}
              style={styles.downloadButton}
              onMouseEnter={(e) => (e.target.style.backgroundColor = "#2563eb")}
              onMouseLeave={(e) => (e.target.style.backgroundColor = "#3b82f6")}
            >
              <FaDownload />
              원본 이미지 다운로드
            </button>
            {info.mark_type === "compound" && (
              <button
                onClick={() => handleCompoundImgDownload()}
                style={styles.downloadButton}
                onMouseEnter={(e) => (e.target.style.backgroundColor = "#2563eb")}
                onMouseLeave={(e) => (e.target.style.backgroundColor = "#3b82f6")}
              >
                <FaDownload />
                복합 이미지 다운로드
              </button>
            )}
          </div>
        </div>
      </div>
    );
  };

  const handleImgDownload = async () => {
    try {
      const res = await getFileDownload(info.image_filename, info.image_original_filename);
      const blob = new Blob([res.data], { type: res.headers["content-type"] });
      const url = window.URL.createObjectURL(blob);

      const a = document.createElement("a");
      a.href = url;
      a.download = info.image_original_filename || "downloaded_image";
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("원본 이미지 다운로드 실패:", error);
    }
  };

  const handleCompoundImgDownload = () => {
    if (!originalImageUrl || !info?.image_original_filename) {
      alert("다운로드할 이미지가 없습니다.");
      return;
    }

    const link = document.createElement("a");
    link.download = info.compound_original_filename;
    link.href = imageUrl;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const delSelf = async () => {
    const data = id;
    const result = await selfMultiDelete([data]);
    if (typeof result === "object") {
      setAlertMsg("completeDelete");
      commonModalSetting(setAlertBox, true, "alert", "정상적으로 삭제되었습니다.");

      setTimeout(() => {
        navigate("/self-application");
      }, 500);
    } else return await catchError(result, navigate, setAlertBox, setAlertMsg);
  };

  if (!info) {
    return (
      <div style={styles.container}>
        <div style={{ textAlign: "center", padding: "48px" }}>
          <div style={{ fontSize: "16px", color: "#6b7280" }}>로딩 중...</div>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      {/* 헤더 */}
      <div style={styles.header}>
        <h1 style={styles.title}>
          <FaFileAlt />
          SELF-APPLICATION-DETAIL
          <span style={styles.idBadge}>ID: {id}</span>
        </h1>
      </div>

      {/* 셀프 출원 내역 */}
      <div style={styles.section}>
        <div style={styles.sectionHeader}>
          <h5 style={styles.sectionTitle}>
            <FaFileAlt />
            셀프 출원 내역
          </h5>
        </div>
        <div style={styles.sectionContent}>
          <div style={styles.tableWrapper}>
            <table style={styles.table}>
              <tbody>
                <tr>
                  <td style={{ ...styles.td, ...styles.tdTitle }}>템플릿명</td>
                  <td style={styles.td}>{info?.template_name || "-"}</td>
                  <td style={{ ...styles.td, ...styles.tdTitle }}>사용자 이름</td>
                  <td style={styles.td}>{info?.user_name || "-"}</td>
                </tr>
                <tr>
                  <td style={{ ...styles.td, ...styles.tdTitle }}>상표 유형</td>
                  <td style={styles.td}>
                    <span style={styles.markTypeText}>{markTypeList[info?.mark_type?.toUpperCase()] || info?.mark_type}</span>
                  </td>
                  <td style={{ ...styles.td, ...styles.tdTitle }}>등록일자</td>
                  <td style={styles.td}>
                    <span
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "6px"
                      }}
                    >
                      <FaCalendarAlt size={12} />
                      {info?.created_at?.split(" ")[0] || "-"}
                    </span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* 상표 이미지 */}
      {renderImage()}

      {/* 출원인 정보 */}
      <div style={styles.section}>
        <div style={styles.sectionHeader}>
          <h5 style={styles.sectionTitle}>
            <FaUser />
            출원인 정보
          </h5>
        </div>
        <div style={styles.sectionContent}>
          <div style={styles.tableWrapper}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>No.</th>
                  <th style={styles.th}>이름</th>
                  <th style={styles.th}>특허고객번호</th>
                  <th style={styles.th}>지분</th>
                </tr>
              </thead>
              <tbody>{renderApplicants()}</tbody>
            </table>
          </div>
        </div>
      </div>

      {/* 상표명 */}
      <div style={styles.section}>
        <div style={styles.sectionHeader}>
          <h5 style={styles.sectionTitle}>
            <FaTags />
            상표명
          </h5>
        </div>
        <div style={styles.sectionContent}>
          <div style={styles.tableWrapper}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>단계</th>
                  <th style={styles.th}>상표명</th>
                </tr>
              </thead>
              <tbody>{renderMarks()}</tbody>
            </table>
          </div>
        </div>
      </div>

      {/* 상품/업종 정보 */}
      <div style={styles.section}>
        <div style={styles.sectionHeader}>
          <h5 style={styles.sectionTitle}>
            <FaListAlt />
            상품/업종 정보
          </h5>
        </div>
        <div style={styles.sectionContent}>
          <div style={styles.tableWrapper}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>상품 분류</th>
                  <th style={styles.th}>등록 상품</th>
                </tr>
              </thead>
              <tbody>{renderDetails()}</tbody>
            </table>
          </div>
        </div>
      </div>

      {/* 액션 버튼 */}
      <div style={styles.actionSection}>
        <button
          style={styles.deleteButton}
          onClick={() => {
            setAlertMsg("deleteSelf");
            commonModalSetting(setAlertBox, true, "confirm", "정말 삭제하시겠습니까?");
          }}
          onMouseEnter={(e) => (e.target.style.backgroundColor = "#b91c1c")}
          onMouseLeave={(e) => (e.target.style.backgroundColor = "#dc2626")}
        >
          <FaTrash />
          삭제
        </button>
      </div>

      {/* 플로팅 버튼 - 모바일에서만 보이고, FloatingList가 열리면 숨김 */}
      <button
        style={styles.floatingButton}
        onClick={() => setFloatSideBar(true)}
        onMouseEnter={(e) => {
          if (isMobile && !floatSideBar) {
            e.target.style.transform = "scale(1.15) translateY(-5px)";
            e.target.style.boxShadow = "0 12px 35px rgba(102, 126, 234, 0.6), 0 8px 20px rgba(0, 0, 0, 0.2)";
          }
        }}
        onMouseLeave={(e) => {
          if (isMobile && !floatSideBar) {
            e.target.style.transform = "scale(1) translateY(0)";
            e.target.style.boxShadow = "0 8px 25px rgba(102, 126, 234, 0.4), 0 4px 12px rgba(0, 0, 0, 0.15)";
          }
        }}
      >
        <FaCommentDots
          size={24}
          color="#ffffff"
          style={{
            filter: "drop-shadow(0 2px 4px rgba(0, 0, 0, 0.2))"
          }}
        />
      </button>

      {/* 모달 */}
      {alertBox.bool && (
        <CommonModal
          setModal={setAlertBox}
          modal={alertBox}
          okFn={() => {
            if (alertMsg === "logout") navigate("/");
            else if (alertMsg === "deleteSelf") delSelf();
            else return;
          }}
        />
      )}

      {/* 플로팅 리스트 - 항상 렌더링 */}
      <FloatingList type={"SELF"} id={id} isOpen={floatSideBar} onClose={() => setFloatSideBar(false)} />

      {/* 반응형 스타일 */}
      <style>{`
        @media (max-width: 768px) {
          .table-wrapper {
            -webkit-overflow-scrolling: touch;
          }
          
          .table-wrapper::-webkit-scrollbar {
            height: 6px;
          }
          
          .table-wrapper::-webkit-scrollbar-track {
            background: #f1f5f9;
            border-radius: 3px;
          }
          
          .table-wrapper::-webkit-scrollbar-thumb {
            background: #cbd5e1;
            border-radius: 3px;
          }
        }
      `}</style>
    </div>
  );
};

export default SelfApplicationDetail;
